import React, { useEffect, useRef, useState } from 'react';
import { IonButton, IonText } from '@ionic/react';

const SCRIPT_ID = 'cf-turnstile-script';
const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
// Cap how long we wait for the Turnstile global to appear. A script blocked by an
// ad/privacy blocker (or an offline device) often never fires an `error` event, so
// without a timeout the promise — and the widget — would hang forever, leaving the
// contact form's submit button silently disabled with no explanation.
const LOAD_TIMEOUT_MS = 15000;

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

interface TurnstileAPI {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string;
  remove: (id: string) => void;
  reset: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

// Loads the Turnstile script once and resolves when window.turnstile is ready.
// Resolution is poll-based rather than load-event based: on a remount the
// <script> tag from a previous mount may already have finished loading, in which
// case a freshly attached `load` listener would never fire again. Polling for the
// global sidesteps that, and the timeout surfaces a silently blocked script as a
// rejection instead of an indefinitely pending promise.
function loadTurnstileScript(timeoutMs = LOAD_TIMEOUT_MS): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    if (!document.getElementById(SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('Failed to load Turnstile'));
      document.head.appendChild(script);
    }
    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (window.turnstile) {
        window.clearInterval(interval);
        resolve();
      } else if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(interval);
        reject(new Error('Turnstile load timed out'));
      }
    }, 100);
  });
}

interface TurnstileWidgetProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
}

type WidgetStatus = 'loading' | 'ready' | 'error';

/**
 * Renders a Cloudflare Turnstile widget via explicit rendering. Callbacks are
 * held in refs so the widget is rendered exactly once per siteKey and isn't
 * torn down on every parent re-render.
 *
 * The widget reports its own loading/error state so the contact form never
 * leaves the user staring at a disabled submit button with no idea why: if the
 * challenge can't load (blocked, offline, misconfigured key/domain) we show an
 * explanation and a retry instead of failing silently.
 */
const TurnstileWidget: React.FC<TurnstileWidgetProps> = ({
  siteKey,
  onVerify,
  onExpire,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const callbacksRef = useRef({ onVerify, onExpire, onError });
  callbacksRef.current = { onVerify, onExpire, onError };
  const [status, setStatus] = useState<WidgetStatus>('loading');
  // Bumping this re-runs the effect so "Retry" re-renders the widget from scratch.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: token => callbacksRef.current.onVerify(token),
          'expired-callback': () => callbacksRef.current.onExpire?.(),
          'error-callback': () => {
            if (!cancelled) setStatus('error');
            callbacksRef.current.onError?.();
          },
        });
        if (!cancelled) setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
        callbacksRef.current.onError?.();
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, attempt]);

  return (
    <div>
      <div ref={containerRef} data-testid="turnstile-widget" />
      {status === 'loading' && (
        <IonText color="medium">
          <p style={{ fontSize: '0.85rem', margin: '8px 0 0' }}>
            Loading verification…
          </p>
        </IonText>
      )}
      {status === 'error' && (
        <div style={{ marginTop: '8px' }}>
          <IonText color="danger">
            <p style={{ fontSize: '0.85rem', margin: '0 0 8px' }}>
              Couldn’t load the verification challenge. Check your connection or
              disable any ad/privacy blockers, then retry.
            </p>
          </IonText>
          <IonButton
            size="small"
            fill="outline"
            onClick={() => setAttempt(a => a + 1)}
            data-testid="turnstile-retry"
          >
            Retry
          </IonButton>
        </div>
      )}
    </div>
  );
};

export default TurnstileWidget;
